/*
 * md-editor-redirect.js
 *
 * Builds the redirect URL to https://editor.marketdwellings.com/product/editor
 * (ctx=shopify) for the v2 product page customize CTA, replacing the legacy
 * iframe + postMessage flow in product-customization-v2.js.
 *
 * Responsibilities:
 *   - Ensure the md_account_id cookie exists (mint a v4 GUID if missing).
 *     The editor will mint one too if absent, but creating it pre-redirect
 *     keeps the account stable across the round-trip and gives the cart
 *     a value to set on _Account ID for design-help requests.
 *   - Detect a background color at click time from the active variant:
 *     acrylic-finish keychains use the ACRYLIC_FINISH_COLORS palette
 *     (matched on the "Acrylic Finish" option), all other products use the
 *     OG paper-color set (White, Kraft, Lemon, Carrot, Scarlet, Razzle
 *     Berry, Light Pink, Light Purple, Light Blue, Arctic, Green Apple).
 *     When matched, build a TCO that paints custom_background_color_front
 *     and custom_background_color_back with the resolved hex.
 *   - On #md-customize-btn click, navigate to the editor with the assembled
 *     params (ctx, brand=md, template=sku, store_domain, variant_id, qty,
 *     title, email, tco).
 */
(function () {
  'use strict';

  var EDITOR_BASE = 'https://editor.marketdwellings.com/product/editor';
  // The editor uses store_domain to construct the /cart/add redirect on
  // completion. Hard-coded to match the customer-facing storefront — not
  // shop.permanent_domain (which resolves to the .myshopify.com value).
  var STORE_DOMAIN = 'marketdwellings.com';
  var ACCOUNT_COOKIE_NAME = 'md_account_id';
  var ACCOUNT_COOKIE_DOMAIN = '.marketdwellings.com';
  var ACCOUNT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 2; // 2 years

  // Values that, when seen on a variant option, mark the product as using
  // OG paper-color TCO. Compared case-insensitively against trimmed values.
  var OG_PAPER_COLORS = {
    'white':        '#f7f6fc',
    'kraft':        '#c8a788',
    'lemon':        '#fce300',
    'carrot':       '#FF7E30',
    'scarlet':      '#d01914',
    'razzle berry': '#d53e85',
    'light pink':   '#e39ba0',
    'light purple': '#bdb0cc',
    'light blue':   '#b2cfd1',
    'arctic':       '#5ac6ec',
    'green apple':  '#83DC1F'
  };

  // Acrylic-finish keychains. Matched ONLY against the value of an option
  // literally named "Acrylic Finish" (see getAcrylicFinishValue) so the
  // shared key 'white' never collides with the OG paper-color 'white'.
  // Painted into the same custom_background_color_front element the paper
  // products use — confirmed on the keychain DH templates 2026-05-20.
  var ACRYLIC_FINISH_COLORS = {
    'white':       '#ffffff',
    'black':       '#000000',
    'coconut':     '#ad9482',
    'ivory':       '#efe3d7',
    'nude':        '#e1cfc6',
    'olive':       '#87907f',
    'pastel blue': '#a9c8db',
    'stone':       '#6d7883'
  };

  // ── Cookie helpers ─────────────────────────────────────────

  // Read a cookie value by name; returns '' if missing.
  function readCookie(name) {
    var escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    var m = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  // Generate an RFC4122 v4 GUID, using crypto.randomUUID when available.
  function generateGuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      var v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  // Ensure md_account_id exists on .marketdwellings.com. Returns the value.
  function ensureAccountCookie() {
    var existing = readCookie(ACCOUNT_COOKIE_NAME);
    if (existing) return existing;
    var id = generateGuid();
    // SameSite=Lax so the editor (subdomain) can read it on a top-level navigation.
    document.cookie =
      ACCOUNT_COOKIE_NAME + '=' + encodeURIComponent(id) +
      '; path=/; domain=' + ACCOUNT_COOKIE_DOMAIN +
      '; max-age=' + ACCOUNT_COOKIE_MAX_AGE +
      '; SameSite=Lax';
    return id;
  }

  // ── Variant + paper-color resolution ───────────────────────

  // Resolve the active variant object using window.mdProductData (emitted
  // by main-product-v2.liquid). Falls back to product.variants[0] if the
  // current hidden input value can't be matched.
  function getCurrentVariant() {
    if (!window.mdProductData || !Array.isArray(window.mdProductData.variants)) return null;
    var input = document.getElementById('md-variant-id');
    var currentId = input && input.value ? parseInt(input.value, 10) : null;
    var variants = window.mdProductData.variants;
    if (currentId) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].id === currentId) return variants[i];
      }
    }
    return variants[0] || null;
  }

  // Returns the OG paper color value on the current variant (case-preserving)
  // if any of its option values is in OG_PAPER_COLORS, otherwise null.
  function getActivePaperColor(variant) {
    if (!variant) return null;
    var values = [variant.option1, variant.option2, variant.option3];
    for (var i = 0; i < values.length; i++) {
      var v = values[i];
      if (!v) continue;
      var key = String(v).toLowerCase().trim();
      if (Object.prototype.hasOwnProperty.call(OG_PAPER_COLORS, key)) return v;
    }
    return null;
  }

  // Build a Design Huddle TCO that paints front + back background with the hex.
  function buildPaperColorTco(colorValue) {
    if (!colorValue) return null;
    var hex = OG_PAPER_COLORS[String(colorValue).toLowerCase().trim()];
    if (!hex) return null;
    return {
      elements: {
        custom_background_color_front: { color: hex },
        custom_background_color_back:  { color: hex }
      }
    };
  }

  // Resolve the "Acrylic Finish" option value for the active variant, using
  // the option order in window.mdProductData.options. Returns null when the
  // product has no option named "Acrylic Finish" (paper products, wood
  // keychains) so the acrylic palette is never consulted for them.
  function getAcrylicFinishValue(variant) {
    if (!variant || !window.mdProductData || !Array.isArray(window.mdProductData.options)) return null;
    var opts = window.mdProductData.options;
    for (var i = 0; i < opts.length; i++) {
      if (String(opts[i]).toLowerCase().trim() === 'acrylic finish') {
        return variant['option' + (i + 1)] || null;
      }
    }
    return null;
  }

  // Resolve the background hex for the active variant. Acrylic-finish
  // keychains take precedence (their own palette); otherwise fall back to
  // the OG paper-color palette. Returns a hex string or null.
  function resolveBackgroundHex(variant) {
    if (!variant) return null;
    var finish = getAcrylicFinishValue(variant);
    if (finish) {
      var fkey = String(finish).toLowerCase().trim();
      if (Object.prototype.hasOwnProperty.call(ACRYLIC_FINISH_COLORS, fkey)) {
        return ACRYLIC_FINISH_COLORS[fkey];
      }
    }
    var paperColor = getActivePaperColor(variant);
    if (paperColor) {
      return OG_PAPER_COLORS[String(paperColor).toLowerCase().trim()] || null;
    }
    return null;
  }

  // Build a Design Huddle TCO that paints front + back background with a hex.
  // custom_background_color_back is harmlessly ignored by templates that have
  // no back background element (e.g. single-sided keychains).
  function buildBackgroundTco(hex) {
    if (!hex) return null;
    return {
      elements: {
        custom_background_color_front: { color: hex },
        custom_background_color_back:  { color: hex }
      }
    };
  }

  // ── URL builder ────────────────────────────────────────────

  function buildEditorUrl(opts) {
    var params = [
      'ctx=shopify',
      'brand=md',
      'template=' + encodeURIComponent(opts.template || ''),
      'store_domain=' + encodeURIComponent(opts.storeDomain || ''),
      'variant_id=' + encodeURIComponent(opts.variantId || ''),
      'qty=' + encodeURIComponent(opts.qty || 1)
    ];
    if (opts.email) params.push('email=' + encodeURIComponent(opts.email));
    if (opts.tco)   params.push('tco=' + encodeURIComponent(JSON.stringify(opts.tco)));
    return EDITOR_BASE + '?' + params.join('&');
  }

  // Cart-line quantity = Shopify's input[name="quantity"] on the product
  // form, defaulting to 1 if absent. Do NOT fall back to #md-qty-select —
  // that's a variant-option selector ("Quantity" option used for price-
  // break tiers like 50/100/250), where each variant is itself a multi-pack.
  // The cart line quantity is almost always 1 for those products.
  function getCurrentQty(form) {
    if (!form) return '1';
    var qty = form.querySelector('input[name="quantity"]');
    return qty && qty.value ? qty.value : '1';
  }

  function getCustomerEmail() {
    var el = document.getElementById('customer__email');
    var v = el ? el.textContent.trim() : '';
    return v || '';
  }

  // ── Click handler ──────────────────────────────────────────

  function navigateToEditor(form) {
    var variant = getCurrentVariant();
    if (!variant) return;
    var bgHex = resolveBackgroundHex(variant);
    var tco = bgHex ? buildBackgroundTco(bgHex) : null;

    ensureAccountCookie();

    var url = buildEditorUrl({
      template: variant.sku || '',
      storeDomain: STORE_DOMAIN,
      variantId: variant.id,
      qty: getCurrentQty(form),
      email: getCustomerEmail(),
      tco: tco
    });

    window.location.assign(url);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('#md-customize-btn');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var form = document.getElementById('md-product-form');
    if (!form) return;
    navigateToEditor(form);
  });

  // Mint the account cookie eagerly so any same-page design-help flow has
  // a value to read for the _Account ID line item property.
  ensureAccountCookie();

  // Expose for md-design-help.js (cookie + paper-color readers).
  window.MDEditorRedirect = {
    ensureAccountCookie: ensureAccountCookie,
    readAccountCookie: function () { return readCookie(ACCOUNT_COOKIE_NAME); },
    getCurrentVariant: getCurrentVariant,
    getActivePaperColor: getActivePaperColor,
    buildPaperColorTco: buildPaperColorTco,
    resolveBackgroundHex: resolveBackgroundHex,
    buildBackgroundTco: buildBackgroundTco
  };
})();
